import { useEffect, useState, useCallback } from "react";
import {
  BookOpenCheck, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Loader2, RefreshCw, Search, AlertTriangle, RotateCcw, Check,
  X, Upload, Download, History,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const TOPICS = ["all", "onboarding_process", "application_status", "dashboard",
                "reports", "sandbox_access", "going_live",
                "legal_compliance", "account_team", "other"];

/**
 * Brief 3.4.11 — NeptunePay platform knowledge admin page (super_admin only).
 *
 * Lives at /admin/ops/neptune-platform-knowledge. Mirrors the PayTech
 * FAQ admin shell but operates on neptune_platform_knowledge_entries
 * + npk_id fields.
 */
export default function AdminNeptunePlatformKnowledge() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [topics, setTopics] = useState(TOPICS);
  const [pending, setPending] = useState([]);
  const [topic, setTopic] = useState("all");
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (topic !== "all") params.topic = topic;
      if (q) params.q = q;
      if (activeOnly) params.active_only = true;
      const { data } = await api.get("/admin/platform-knowledge", { params });
      setEntries(data.entries || []);
      setTopics(["all", ...(data.topics || [])]);
      setPending(data.pending_removals || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load entries.");
    } finally {
      setLoading(false);
    }
  }, [topic, q, activeOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  if (user?.role !== "super_admin") {
    return <div data-testid="npk-forbidden" className="text-rose-200">Super-admin only.</div>;
  }

  const onToggle = async (npkId) => {
    try {
      await api.post(`/admin/platform-knowledge/${npkId}/toggle-active`);
      toast.success("Toggled.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle.");
    }
  };

  const onDelete = async (npkId) => {
    if (!window.confirm("Soft-delete this entry? It will be flipped to inactive (data retained).")) return;
    try {
      await api.delete(`/admin/platform-knowledge/${npkId}`);
      toast.success("Deleted.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete.");
    }
  };

  const onImport = async (markdown) => {
    setImporting(true);
    try {
      const { data } = await api.post(
        "/admin/platform-knowledge/_/import-markdown",
        { markdown },
      );
      setImportResult({ open: true, ...data });
      toast.success(`Imported · created ${data.created.length} · updated ${data.updated.length} · soft-deleted ${data.soft_deleted.length}`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import failed.";
      setImportResult({ open: true, error: detail });
      toast.error(detail);
    } finally {
      setImporting(false);
    }
  };

  const onExport = async () => {
    try {
      const { data } = await api.get("/admin/platform-knowledge/_/export-markdown");
      await navigator.clipboard.writeText(data.markdown);
      toast.success(`Markdown copied (${data.count} entries).`);
    } catch (e) {
      toast.error("Could not copy markdown.");
    }
  };

  const onConfirmRemoval = async (npkId) => {
    try {
      await api.post(`/admin/platform-knowledge/${npkId}/confirm-removal`);
      toast.success("Removal confirmed.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm.");
    }
  };

  const onRestore = async (npkId) => {
    try {
      await api.post(`/admin/platform-knowledge/${npkId}/restore`);
      toast.success("Restored.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore.");
    }
  };

  return (
    <div data-testid="npk-page" className="max-w-6xl space-y-6 pb-16">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <BookOpenCheck className="h-4 w-4 text-cyan-300/80" />
            <h1 className="text-[18px] tracking-wide text-white">Platform knowledge</h1>
          </div>
          <p className="mt-2 text-[12.5px] text-zinc-400 max-w-2xl">
            Curated answers Nerea serves for NeptunePay platform questions
            (onboarding, status, dashboard, reports, sandbox, going-live,
            legal, account-team). High-confidence matches (≥ 0.80)
            short-circuit the brain alongside PayTech FAQ — ties favour the
            FAQ matcher.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onExport} data-testid="npk-export-markdown" className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Download className="h-3 w-3" /> Export markdown
          </button>
          <button onClick={() => setImportResult({ open: true })} data-testid="npk-open-import" className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] px-3 py-1.5 text-[11.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Upload className="h-3 w-3" /> Import markdown
          </button>
          <button onClick={() => setEditing({})} data-testid="npk-new" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[11.5px] text-cyan-100 hover:bg-cyan-400/[0.14]">
            <Plus className="h-3 w-3" /> New entry
          </button>
        </div>
      </header>

      {pending.length > 0 && (
        <div data-testid="npk-pending-removals-banner" className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] p-4 text-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />
            <div className="text-[13px]">
              {pending.length} entries removed from source markdown — review before they stay inactive
            </div>
          </div>
          <ul className="space-y-1.5 text-[12.5px]">
            {pending.map((e) => (
              <li key={e.npk_id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="truncate">
                  <span className="text-zinc-500 font-mono mr-1">{e.npk_id}</span>
                  · {e.topic} · {e.question}
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => onRestore(e.npk_id)} data-testid={`npk-restore-${e.npk_id}`} className="inline-flex items-center gap-1 rounded border border-emerald-400/30 bg-emerald-400/[0.06] px-2 py-0.5 text-[10.5px] text-emerald-100">
                    <RotateCcw className="h-3 w-3" /> Restore
                  </button>
                  <button onClick={() => onConfirmRemoval(e.npk_id)} data-testid={`npk-confirm-removal-${e.npk_id}`} className="inline-flex items-center gap-1 rounded border border-rose-400/30 bg-rose-400/[0.06] px-2 py-0.5 text-[10.5px] text-rose-100">
                    <Check className="h-3 w-3" /> Confirm removal
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {topics.map((t) => (
          <button
            key={t}
            onClick={() => setTopic(t)}
            data-testid={`npk-topic-${t}`}
            className={`rounded-full border px-3 py-1 text-[11.5px] ${topic === t ? "border-white/[0.20] bg-white/[0.08] text-white" : "border-white/[0.08] bg-white/[0.02] text-zinc-400 hover:bg-white/[0.04]"}`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[11.5px] text-zinc-400 flex items-center gap-1.5">
            <input type="checkbox" data-testid="npk-active-only" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
            Active only
          </label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <input
              type="text"
              placeholder="Search Q/A…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="npk-search"
              className="rounded-md border border-white/[0.08] bg-black/30 pl-7 pr-2 py-1 text-[12px] text-white"
            />
          </div>
          <button onClick={refresh} data-testid="npk-refresh" className="inline-flex items-center gap-1 text-[11.5px] text-zinc-400 hover:text-white">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div data-testid="npk-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">{error}</div>}

      {entries.length === 0 && !loading && (
        <div data-testid="npk-empty" className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-6 text-zinc-500 text-[12.5px]">
          No entries match the current filter.
        </div>
      )}

      <ul className="space-y-2">
        {entries.map((e) => (
          <NPKRow
            key={e.npk_id}
            entry={e}
            onEdit={() => setEditing(e)}
            onToggle={() => onToggle(e.npk_id)}
            onDelete={() => onDelete(e.npk_id)}
            onShowHistory={() => setHistoryFor(e)}
          />
        ))}
      </ul>

      {editing && <EditModal entry={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
      {historyFor && <HistoryDrawer entry={historyFor} onClose={() => setHistoryFor(null)} />}
      {importResult?.open && <ImportModal onClose={() => setImportResult(null)} onImport={onImport} importing={importing} lastResult={importResult.created !== undefined ? importResult : null} lastError={importResult.error || null} />}
    </div>
  );
}

function NPKRow({ entry, onEdit, onToggle, onDelete, onShowHistory }) {
  return (
    <li
      data-testid={`npk-row-${entry.npk_id}`}
      className={`rounded-xl border ${entry.active ? "border-white/[0.06] bg-white/[0.015]" : "border-zinc-700/40 bg-zinc-800/[0.18]"} p-4`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full border ${entry.active ? "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-100" : "border-zinc-500/30 bg-zinc-500/[0.08] text-zinc-400"} px-2 py-0.5 text-[10.5px]`}>
              {entry.active ? "active" : "inactive"}
            </span>
            <span className="rounded border border-white/[0.08] bg-white/[0.02] px-2 py-0.5 text-[10.5px] text-zinc-400 font-mono">
              {entry.topic}
            </span>
            <span className="text-[10.5px] text-zinc-500 font-mono">v{entry.version} · {entry.npk_id}</span>
          </div>
          <div className="mt-2 text-[13px] text-zinc-100 leading-snug">{entry.question}</div>
          <div className="mt-1 text-[12px] text-zinc-400 whitespace-pre-wrap break-words">{entry.answer}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button data-testid={`npk-edit-${entry.npk_id}`} onClick={onEdit} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
            <Pencil className="h-3 w-3" /> Edit
          </button>
          {entry.version > 1 && (
            <button data-testid={`npk-history-${entry.npk_id}`} onClick={onShowHistory} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
              <History className="h-3 w-3" /> v{entry.version}
            </button>
          )}
          <button data-testid={`npk-toggle-${entry.npk_id}`} onClick={onToggle} className="inline-flex items-center gap-1 rounded-md border border-white/[0.10] px-2 py-1 text-[10.5px] text-zinc-300 hover:bg-white/[0.04]">
            {entry.active ? <ToggleRight className="h-3 w-3 text-emerald-300" /> : <ToggleLeft className="h-3 w-3 text-zinc-500" />}
          </button>
          <button data-testid={`npk-delete-${entry.npk_id}`} onClick={onDelete} className="inline-flex items-center gap-1 rounded-md border border-rose-400/20 px-2 py-1 text-[10.5px] text-rose-200 hover:bg-rose-400/[0.06]">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
    </li>
  );
}

function EditModal({ entry, onClose, onSaved }) {
  const isCreate = !entry.npk_id;
  const [question, setQuestion] = useState(entry.question || "");
  const [answer, setAnswer] = useState(entry.answer || "");
  const [topic, setTopic] = useState(entry.topic || "onboarding_process");
  const [active, setActive] = useState(entry.active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Question + answer required.");
      return;
    }
    setSaving(true);
    try {
      if (isCreate) {
        await api.post("/admin/platform-knowledge", { question, answer, topic, active });
        toast.success("Entry created.");
      } else {
        await api.patch(`/admin/platform-knowledge/${entry.npk_id}`, { question, answer, topic, active });
        toast.success("Entry updated.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="npk-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] text-white">{isCreate ? "New platform-knowledge entry" : `Edit ${entry.npk_id}`}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <Field label="Question">
          <textarea data-testid="npk-edit-question" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white min-h-[60px]" value={question} onChange={(e) => setQuestion(e.target.value)} />
        </Field>
        <Field label="Answer">
          <textarea data-testid="npk-edit-answer" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white min-h-[140px]" value={answer} onChange={(e) => setAnswer(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Topic">
            <select data-testid="npk-edit-topic" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-2 text-[13px] text-white" value={topic} onChange={(e) => setTopic(e.target.value)}>
              {TOPICS.filter((t) => t !== "all").map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-[12.5px] text-zinc-300 pt-6">
            <input type="checkbox" data-testid="npk-edit-active" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-md border border-white/[0.10] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.04]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="npk-edit-save" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[12px] text-cyan-100 hover:bg-cyan-400/[0.14] disabled:opacity-50">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function HistoryDrawer({ entry, onClose }) {
  return (
    <div data-testid="npk-history-drawer" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] text-white">Version history · {entry.npk_id}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <ul className="space-y-3 text-[12.5px]">
          {[...(entry.version_history || [])].reverse().map((h) => (
            <li key={h.version} className="rounded-md border border-white/[0.06] bg-white/[0.01] p-3">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">v{h.version} · {h.edited_at} · {h.edited_by_email || h.edited_by_user_id}</div>
              <div className="mt-1 text-zinc-400 italic">{h.diff_summary}</div>
              <details className="mt-2">
                <summary className="cursor-pointer text-zinc-300">show content</summary>
                <div className="mt-2 text-zinc-200"><span className="text-zinc-500">Q: </span>{h.question}</div>
                <div className="mt-1 text-zinc-200 whitespace-pre-wrap break-words"><span className="text-zinc-500">A: </span>{h.answer}</div>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onImport, importing, lastResult, lastError }) {
  const [md, setMd] = useState("");
  return (
    <div data-testid="npk-import-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-2xl border border-white/[0.10] bg-zinc-950 p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[14px] text-white">Import markdown</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        {/* neptune-allow: literal filename neptune_platform_knowledge_v1.md displayed verbatim to the operator as an import-format hint */}
        <p className="text-[12px] text-zinc-400">
          Paste the neptune_platform_knowledge_v1.md content. Format is strict —
          <code className="mx-1 text-[11px] font-mono">## topic_name</code>
          headers, then
          <code className="mx-1 text-[11px] font-mono">*Q:*</code>
          /
          <code className="mx-1 text-[11px] font-mono">*A:*</code>
          line prefixes. Existing entries match by inline
          <code className="mx-1 text-[11px] font-mono">&lt;!-- npk_id: ... --&gt;</code>
          comments. Entries absent from the markdown will be soft-deleted with a
          review banner before they stay inactive.
        </p>
        <textarea data-testid="npk-import-textarea" className="w-full rounded-md border border-white/[0.08] bg-black/30 p-3 text-[12px] text-white font-mono min-h-[280px]" value={md} onChange={(e) => setMd(e.target.value)} placeholder="## onboarding_process&#10;*Q:* What documents are required?&#10;*A:* The core KYB pack…&#10;" />
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-white/[0.10] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.04]">Cancel</button>
          <button onClick={() => onImport(md)} disabled={importing || !md.trim()} data-testid="npk-import-submit" className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[12px] text-cyan-100 disabled:opacity-50">
            {importing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Import
          </button>
        </div>
        {lastError && (
          <div data-testid="npk-import-error" className="mt-3 rounded-md border border-rose-400/25 bg-rose-400/[0.04] p-3 text-[12px] text-rose-200">
            <div className="text-rose-100 mb-1 font-medium">Import failed</div>
            <div className="text-zinc-400 font-mono whitespace-pre-wrap break-words">{String(lastError)}</div>
            <div className="mt-2 text-[11px] text-zinc-500">
              The parser needs <code className="font-mono">## topic</code> headers and
              <code className="mx-1 font-mono">*Q:*</code>
              /
              <code className="mx-1 font-mono">*A:*</code>
              line prefixes. Heading-style (## Q: …) is not accepted.
            </div>
          </div>
        )}
        {lastResult && (
          <div data-testid="npk-import-result" className="mt-3 rounded-md border border-emerald-400/25 bg-emerald-400/[0.04] p-3 text-[12px]">
            <div className="text-emerald-100 mb-1">Import done</div>
            <div className="text-zinc-400">created: {lastResult.created.length} · updated: {lastResult.updated.length} · unchanged: {lastResult.unchanged.length} · soft-deleted: {lastResult.soft_deleted.length}</div>
            <details className="mt-2">
              <summary className="cursor-pointer text-zinc-300">show updated markdown (paste back into source file)</summary>
              <pre className="mt-2 rounded border border-white/[0.06] bg-black/30 p-2 text-[11px] text-zinc-300 overflow-x-auto whitespace-pre-wrap">{lastResult.updated_markdown}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
